/**
 * Utility functions for tracking events across multiple platforms
 * Facebook Pixel, Google Analytics, GTM
 */

// Declare global window extensions for TypeScript
declare global {
  interface Window {
    fbq?: (action: string, eventName: string, params?: Record<string, any>) => void;
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Track CTA Click Event
 * @param ctaId - Unique identifier for the CTA button
 * @param ctaText - Text content of the CTA
 * @param location - Where on page the CTA is located (hero, pricing, footer, etc.)
 */
export const trackCTAClick = (ctaId: string, ctaText: string, location: string) => {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'CTAClick', {
      cta_id: ctaId,
      cta_text: ctaText,
      cta_location: location,
      page_url: window.location.href
    });
  }
  
  // Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cta_click', {
      event_category: 'engagement',
      event_label: ctaId,
      cta_text: ctaText,
      cta_location: location
    });
  }
  
  // GTM dataLayer
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'ctaClick',
      ctaId: ctaId,
      ctaText: ctaText,
      ctaLocation: location
    });
  }
  
  console.log('🎯 CTA Click Tracked:', { ctaId, ctaText, location });
};

/**
 * Track Modal Open Event
 * @param modalType - Type of modal (activation, trial, exit-intent, etc.)
 * @param trigger - What triggered the modal (button, timer, exit-intent, etc.)
 */
export const trackModalOpen = (modalType: string, trigger: string) => {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'ModalOpen', {
      modal_type: modalType,
      trigger: trigger,
      timestamp: new Date().toISOString()
    });
  }
  
  // Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'modal_open', {
      event_category: 'engagement',
      event_label: modalType,
      trigger: trigger
    });
  }
  
  // GTM dataLayer
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'modalOpen',
      modalType: modalType,
      trigger: trigger
    });
  }
  
  console.log('📋 Modal Open Tracked:', { modalType, trigger });
};

/**
 * Track Video Play Event
 * @param videoId - Unique identifier for the video
 * @param videoTitle - Title/description of video
 * @param playPercentage - Percentage of video watched (0-100)
 */
export const trackVideoPlay = (videoId: string, videoTitle: string, playPercentage: number) => {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'VideoPlay', {
      video_id: videoId,
      video_title: videoTitle,
      play_percentage: playPercentage
    });
  }
  
  // Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'video_play', {
      event_category: 'engagement',
      event_label: videoId,
      video_title: videoTitle,
      play_percentage: playPercentage
    });
  }
  
  console.log('▶️ Video Play Tracked:', { videoId, videoTitle, playPercentage });
};

/**
 * Track Plan Selection Event
 * @param planName - Name of the plan selected
 * @param planPrice - Price of the plan
 * @param planType - Type of plan (starter, enterprise, trial)
 */
export const trackPlanSelection = (planName: string, planPrice: number, planType: string) => {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'PlanSelected', {
      plan_name: planName,
      plan_price: planPrice,
      plan_type: planType,
      currency: 'MXN'
    });
  }
  
  // Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'plan_selection', {
      event_category: 'conversion',
      event_label: planName,
      value: planPrice,
      currency: 'MXN'
    });
  }
  
  console.log('💳 Plan Selection Tracked:', { planName, planPrice, planType });
};

/**
 * Track Popup Interaction Event
 * @param popupType - Type of popup (urgency, social-proof, exit-intent, etc.)
 * @param action - Action taken (viewed, clicked, closed, converted)
 */
export const trackPopupInteraction = (popupType: string, action: string) => {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'PopupInteraction', {
      popup_type: popupType,
      action: action,
      timestamp: new Date().toISOString()
    });
  }
  
  // Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'popup_interaction', {
      event_category: 'engagement',
      event_label: popupType,
      action: action
    });
  }
  
  console.log('🔔 Popup Interaction Tracked:', { popupType, action });
};

/**
 * Track FAQ Interaction Event
 * @param question - The FAQ question clicked
 * @param questionIndex - Index/position of the question
 */
export const trackFAQInteraction = (question: string, questionIndex: number) => {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'FAQInteraction', {
      question: question,
      question_index: questionIndex
    });
  }
  
  // Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'faq_click', {
      event_category: 'engagement',
      event_label: question,
      question_index: questionIndex
    });
  }
  
  console.log('❓ FAQ Interaction Tracked:', { question, questionIndex });
};

/**
 * Track Section View Event (when user scrolls into view)
 * @param sectionName - Name of the section viewed
 * @param sectionId - HTML ID of the section
 */
export const trackSectionView = (sectionName: string, sectionId: string) => {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'SectionView', {
      section_name: sectionName,
      section_id: sectionId
    });
  }
  
  // Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'section_view', {
      event_category: 'engagement',
      event_label: sectionName,
      section_id: sectionId
    });
  }
  
  console.log('👁️ Section View Tracked:', { sectionName, sectionId });
};

/**
 * Track Form Field Interaction
 * @param fieldName - Name of the form field
 * @param fieldValue - Value entered (sanitized)
 * @param formType - Type of form (activation, contact, trial)
 */
export const trackFormFieldInteraction = (fieldName: string, fieldValue: string, formType: string) => {
  // Sanitize sensitive data
  const sanitizedValue = fieldName === 'email' || fieldName === 'phone' ? '[REDACTED]' : fieldValue;
  
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'FormFieldInteraction', {
      field_name: fieldName,
      field_value: sanitizedValue,
      form_type: formType
    });
  }
  
  console.log('📝 Form Field Interaction Tracked:', { fieldName, formType });
};

/**
 * Track Page Exit Intent
 * @param exitLocation - Where user's cursor exited (top, left, right)
 * @param timeOnPage - How long user was on page before exit intent (seconds)
 */
export const trackExitIntent = (exitLocation: string, timeOnPage: number) => {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', 'ExitIntent', {
      exit_location: exitLocation,
      time_on_page: timeOnPage,
      timestamp: new Date().toISOString()
    });
  }
  
  // Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'exit_intent', {
      event_category: 'engagement',
      event_label: exitLocation,
      value: timeOnPage
    });
  }
  
  console.log('🚪 Exit Intent Tracked:', { exitLocation, timeOnPage });
};

/**
 * Initialize time on page tracking
 * Returns function to get elapsed seconds
 */
export const initTimeOnPageTracking = () => {
  const startTime = Date.now();
  
  return () => {
    return Math.floor((Date.now() - startTime) / 1000);
  };
};

/**
 * Track Contact Information Submitted
 * @param contactType - Type of contact (email, phone, whatsapp)
 */
export const trackContactSubmit = (contactType: string) => {
  // Facebook Pixel - Contact Event
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Contact', {
      contact_type: contactType
    });
  }
  
  console.log('📞 Contact Submit Tracked:', { contactType });
};
